import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { programmeHref } from "../../helpers/programmeHref";
import {
  COUNTRY_OPTIONS,
  countryLabel,
} from "../../helpers/advisorVocab";
import {
  appendLocalChat,
  clearChat,
  clearMatches,
  fetchAdvisorProfile,
  fetchAdvisorQuota,
  runAdvisorChat,
  runAdvisorMatch,
  saveAdvisorProfile,
} from "../../redux/slices/advisor";

const DEGREE_LEVELS = [
  "bachelor",
  "master",
  "phd",
  "diploma",
  "certificate",
  "foundation",
  "other",
];

const FIELDS = [
  "computer_science",
  "data_science",
  "engineering",
  "business",
  "economics",
  "finance",
  "law",
  "medicine_health",
  "nursing",
  "pharmacy",
  "natural_sciences",
  "mathematics",
  "social_sciences",
  "psychology",
  "arts_humanities",
  "education",
  "architecture",
  "design",
  "media_communications",
  "agriculture",
  "environment",
  "other",
];

const LOOKING_FOR = [
  { value: "both", label: "Universities & scholarships" },
  { value: "universities", label: "Universities" },
  { value: "scholarships", label: "Scholarships" },
];

const emptyProfile = {
  currentEducationLevel: "",
  intendedDegreeLevel: "master",
  lookingFor: "both",
  fieldsOfInterest: ["computer_science"],
  targetCountries: ["DE"],
  budget: { maxUSD: 15000, currency: "USD", raw: "" },
  tests: { ielts: null, toefl: null, gre: null, gmat: null, gpa: null },
};

const labelize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function schoolHref(id) {
  return id ? `/school/${id}?isDraft=false` : null;
}

function scholarshipHref(id) {
  return id ? `/scholarship/${id}?isDraft=false` : null;
}

function programmesHref(schoolId, programmeId) {
  return programmeHref({
    schoolId,
    programmeId,
    isDraft: false,
  });
}

function formatDeadline(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function moneyText(raw, minUSD) {
  if (raw) return raw;
  if (minUSD != null) return `From $${minUSD}`;
  return null;
}

function FactRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-24 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );
}

const MatchPackageCard = ({ m }) => {
  const isSchool = m.recordType !== "scholarship";
  const href = isSchool
    ? schoolHref(m.recordId)
    : scholarshipHref(m.recordId);
  const fitPct = Math.round((m.score || 0) * 100);
  const counts = m.counts || {};
  const countLine = [
    counts.programmes
      ? `${counts.programmes} programme${counts.programmes === 1 ? "" : "s"}`
      : null,
    counts.scholarships
      ? `${counts.scholarships} scholarship${
          counts.scholarships === 1 ? "" : "s"
        }`
      : null,
    counts.universities
      ? `${counts.universities} universit${
          counts.universities === 1 ? "y" : "ies"
        }`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (m.gated) {
    return (
      <li>
        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
          <div className="p-4 blur-[2px] select-none pointer-events-none opacity-70">
            <p className="font-semibold text-gray-800">
              {m.snapshot?.title || "Match"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {countryLabel(m.snapshot?.country) || "—"}
              {countLine ? ` · ${countLine} inside` : ""}
            </p>
            <div className="mt-3 h-16 rounded-lg bg-gray-200" />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 px-4 text-center">
            <p className="text-sm font-medium text-gray-800">
              Unlock your full shortlist
            </p>
            <p className="text-xs text-gray-600 mt-1">
              See programmes, scholarships, and why this fits.
            </p>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex gap-3 p-4">
        {m.snapshot?.thumbnail ? (
          <img
            src={m.snapshot.thumbnail}
            alt=""
            className="w-16 h-16 rounded-lg object-cover bg-gray-200 shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-gray-200 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
              {isSchool ? "University" : "Scholarship"}
            </span>
            {m.fitLabel && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-800">
                {m.fitLabel}
              </span>
            )}
            <span className="text-[11px] text-gray-400">{fitPct}% match</span>
          </div>
          {href ? (
            <Link
              to={href}
              className="mt-1 block font-semibold text-gray-900 hover:text-purple-700"
            >
              {m.snapshot?.title || "Untitled"}
            </Link>
          ) : (
            <p className="mt-1 font-semibold text-gray-900">
              {m.snapshot?.title || "Untitled"}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-0.5">
            {[
              countryLabel(m.snapshot?.country),
              m.snapshot?.universityType,
              m.snapshot?.ranking,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>

      <div className="px-4 pb-3 space-y-1">
        <FactRow label="Budget" value={m.facts?.budget} />
        <FactRow label="Deadline" value={m.facts?.deadline} />
        <FactRow label="Funding" value={m.facts?.scholarship} />
      </div>

      {m.reasons?.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Why this fits you
          </p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
            {m.reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {(m.strengths?.length > 0 || m.concerns?.length > 0) && (
        <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {m.strengths?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-teal-700 uppercase mb-1">
                Strengths
              </p>
              <ul className="text-sm text-gray-700 space-y-0.5">
                {m.strengths.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {m.concerns?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-700 uppercase mb-1">
                Things to consider
              </p>
              <ul className="text-sm text-gray-700 space-y-0.5">
                {m.concerns.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {m.programmes?.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Programmes at this university
          </p>
          <ul className="space-y-1.5">
            {m.programmes.map((p) => (
              <li
                key={p.recordId}
                className="text-sm text-gray-800 rounded-lg bg-gray-50 px-3 py-2"
              >
                <Link
                  to={programmesHref(m.recordId, p.recordId)}
                  className="font-medium hover:text-purple-700"
                >
                  {p.title}
                </Link>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[
                    p.degreeLevel ? labelize(p.degreeLevel) : null,
                    moneyText(p.tuitionRaw, p.tuitionMinUSD),
                    p.durationMonths ? `${p.durationMonths} months` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {m.scholarships?.length > 0 && isSchool && (
        <div className="px-4 pb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Scholarships that can apply here
          </p>
          <ul className="space-y-1.5">
            {m.scholarships.map((s) => (
              <li
                key={s.recordId}
                className="text-sm text-gray-800 rounded-lg bg-purple-50/60 px-3 py-2"
              >
                <Link
                  to={scholarshipHref(s.recordId)}
                  className="font-medium hover:text-purple-700"
                >
                  {s.title}
                </Link>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[
                    moneyText(s.amountRaw, s.amountMinUSD),
                    formatDeadline(s.deadlineAt)
                      ? `Deadline ${formatDeadline(s.deadlineAt)}`
                      : null,
                    s.fitNote,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {m.universities?.length > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Universities this can work with
          </p>
          <ul className="space-y-1.5">
            {m.universities.map((u) => (
              <li key={u.recordId}>
                <Link
                  to={schoolHref(u.recordId)}
                  className="text-sm font-medium text-gray-800 hover:text-purple-700"
                >
                  {u.title}
                  {u.country ? ` · ${countryLabel(u.country)}` : ""}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

const AiAdvisor = () => {
  const dispatch = useDispatch();
  const {
    profile,
    matches,
    gate,
    quota,
    conversationId,
    chatMessages,
    matchLoading,
    chatLoading,
    error,
  } = useSelector((state) => state.advisor);

  const [form, setForm] = useState(emptyProfile);
  const [refinement, setRefinement] = useState("");
  const [chatInput, setChatInput] = useState("");
  const chatInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchAdvisorProfile());
    dispatch(fetchAdvisorQuota());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setForm({
        currentEducationLevel: profile.currentEducationLevel || "",
        intendedDegreeLevel: profile.intendedDegreeLevel || "master",
        lookingFor: profile.lookingFor || "both",
        fieldsOfInterest: profile.fieldsOfInterest || [],
        targetCountries: profile.targetCountries || [],
        budget: {
          maxUSD: profile.budget?.maxUSD ?? 15000,
          currency: profile.budget?.currency || "USD",
          raw: profile.budget?.raw || "",
        },
        tests: {
          ielts: profile.tests?.ielts ?? null,
          toefl: profile.tests?.toefl ?? null,
          gre: profile.tests?.gre ?? null,
          gmat: profile.tests?.gmat ?? null,
          gpa: profile.tests?.gpa ?? null,
        },
      });
    }
  }, [profile]);

  const quotaLabel = useMemo(() => {
    if (!quota) return "Quota unknown";
    const msgs =
      quota.messagesRemaining == null
        ? "—"
        : `${quota.messagesRemaining} msgs left`;
    const matchesLeft =
      quota.matchRunsRemaining == null
        ? "unlimited matches"
        : `${quota.matchRunsRemaining} matches left`;
    return `${quota.tier || "free"} · ${msgs} · ${matchesLeft}`;
  }, [quota]);

  const toggleMulti = (key, value) => {
    setForm((prev) => {
      const list = prev[key] || [];
      const next = list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value];
      return { ...prev, [key]: next };
    });
  };

  const buildPayload = () => ({
    currentEducationLevel: form.currentEducationLevel || null,
    intendedDegreeLevel: form.intendedDegreeLevel || null,
    lookingFor: form.lookingFor || "both",
    fieldsOfInterest: form.fieldsOfInterest,
    targetCountries: form.targetCountries,
    budget: {
      maxUSD:
        form.budget.maxUSD === "" || form.budget.maxUSD == null
          ? null
          : Number(form.budget.maxUSD),
      currency: form.budget.currency || null,
      raw: form.budget.raw || null,
    },
    tests: {
      ielts: form.tests.ielts === "" ? null : form.tests.ielts,
      toefl: form.tests.toefl === "" ? null : form.tests.toefl,
      gre: form.tests.gre === "" ? null : form.tests.gre,
      gmat: form.tests.gmat === "" ? null : form.tests.gmat,
      gpa: form.tests.gpa === "" ? null : form.tests.gpa,
    },
  });

  const handleSaveAndMatch = async () => {
    const payload = buildPayload();
    const saved = await dispatch(saveAdvisorProfile(payload));
    if (saveAdvisorProfile.fulfilled.match(saved)) {
      const refinements = refinement.trim() ? [refinement.trim()] : [];
      await dispatch(runAdvisorMatch({ refinements }));
      dispatch(fetchAdvisorQuota());
    }
  };

  const handleChatSend = async (e) => {
    e.preventDefault();
    const message = chatInput.trim();
    if (!message) return;
    dispatch(appendLocalChat({ role: "user", content: message }));
    setChatInput("");
    await dispatch(
      runAdvisorChat({
        message,
        conversationId: conversationId || undefined,
        lastMatchIds: matches.map((m) => m.recordId).filter(Boolean),
      })
    );
    dispatch(fetchAdvisorQuota());
  };

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Study Advisor
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Universities first, with programmes and scholarships that can
              apply there.
            </p>
          </div>
          <div className="text-sm px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
            {quotaLabel}
          </div>
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm">
            {String(error)}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Profile */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              1. Academic profile
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-sm text-gray-600">
                Current level
                <select
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  value={form.currentEducationLevel}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      currentEducationLevel: e.target.value,
                    }))
                  }
                >
                  <option value="">—</option>
                  {DEGREE_LEVELS.map((d) => (
                    <option key={d} value={d}>
                      {labelize(d)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-gray-600">
                Intended level
                <select
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  value={form.intendedDegreeLevel}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      intendedDegreeLevel: e.target.value,
                    }))
                  }
                >
                  {DEGREE_LEVELS.map((d) => (
                    <option key={d} value={d}>
                      {labelize(d)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-gray-600">
                Budget max (USD)
                <input
                  type="number"
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  value={form.budget.maxUSD ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      budget: {
                        ...p.budget,
                        maxUSD:
                          e.target.value === "" ? "" : Number(e.target.value),
                      },
                    }))
                  }
                />
              </label>

              <label className="text-sm text-gray-600">
                IELTS
                <input
                  type="number"
                  step="0.5"
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  value={form.tests.ielts ?? ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      tests: {
                        ...p.tests,
                        ielts:
                          e.target.value === "" ? null : Number(e.target.value),
                      },
                    }))
                  }
                />
              </label>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">I am looking for</p>
              <div className="flex flex-wrap gap-2">
                {LOOKING_FOR.map((opt) => {
                  const on = form.lookingFor === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() =>
                        setForm((p) => ({ ...p, lookingFor: opt.value }))
                      }
                      className={`text-xs px-3 py-1.5 rounded-full border ${
                        on
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-gray-600 border-gray-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">Fields of interest</p>
              <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                {FIELDS.map((f) => {
                  const on = form.fieldsOfInterest.includes(f);
                  return (
                    <button
                      type="button"
                      key={f}
                      onClick={() => toggleMulti("fieldsOfInterest", f)}
                      className={`text-xs px-2 py-1 rounded-full border ${
                        on
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-gray-600 border-gray-300"
                      }`}
                    >
                      {labelize(f)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">Target countries</p>
              <div className="flex flex-wrap gap-2">
                {COUNTRY_OPTIONS.map(({ code, name }) => {
                  const on = form.targetCountries.includes(code);
                  return (
                    <button
                      type="button"
                      key={code}
                      onClick={() => toggleMulti("targetCountries", code)}
                      className={`text-xs px-2 py-1 rounded-full border ${
                        on
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-gray-600 border-gray-300"
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block text-sm text-gray-600">
              Refinement (optional)
              <input
                className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                placeholder='e.g. "cheaper" or "add IELTS 7"'
                value={refinement}
                onChange={(e) => setRefinement(e.target.value)}
              />
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                disabled={matchLoading}
                onClick={handleSaveAndMatch}
                className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50"
              >
                {matchLoading ? "Finding matches…" : "See my matches"}
              </button>
              <button
                type="button"
                onClick={() => dispatch(clearMatches())}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700"
              >
                Clear matches
              </button>
            </div>
          </div>

          {/* Matches */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 dark:text-white">
                2. Match results
              </h3>
              {gate && (
                <span className="text-xs text-gray-500">
                  {gate.lockedCount > 0
                    ? `${gate.shownFull} unlocked · ${gate.lockedCount} more in your full shortlist`
                    : `${gate.shownFull} matches`}
                </span>
              )}
            </div>

            {matches.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                Save your profile and run Match to see universities with
                programmes and scholarships that fit.
              </p>
            ) : (
              <ul className="space-y-4 max-h-[40rem] overflow-y-auto pr-1">
                {matches.map((m) => (
                  <MatchPackageCard
                    key={`${m.recordType}-${m.recordId}`}
                    m={m}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Chat */}
          <div className="xl:col-span-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 relative z-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 dark:text-white">
                3. Chat refine
              </h3>
              <button
                type="button"
                onClick={() => dispatch(clearChat())}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                New conversation
              </button>
            </div>

            <div
              role="log"
              aria-live="polite"
              onClick={() => chatInputRef.current?.focus()}
              className="h-56 overflow-y-auto border border-gray-100 rounded-lg p-3 mb-3 bg-gray-50 space-y-3 cursor-text"
            >
              {chatMessages.length === 0 ? (
                <p className="text-sm text-gray-400 italic pointer-events-none">
                  Ask about the universities above — e.g. “Which of those is
                  more affordable?” or “Any of these in Switzerland?”
                </p>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`text-sm max-w-[85%] px-3 py-2 rounded-lg ${
                      msg.role === "user"
                        ? "ml-auto bg-purple-600 text-white"
                        : "bg-white border border-gray-200 text-gray-800"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))
              )}
              {chatLoading && (
                <p className="text-xs text-gray-400 italic">Advisor is typing…</p>
              )}
            </div>

            <form
              onSubmit={handleChatSend}
              className="flex gap-2 items-stretch"
            >
              <input
                ref={chatInputRef}
                type="text"
                autoComplete="off"
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-purple-500 focus:ring-purple-500 disabled:bg-gray-100"
                placeholder="Type a message and press Send…"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                disabled={chatLoading}
              />
              <button
                type="submit"
                disabled={chatLoading || !chatInput.trim()}
                className="px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50 shrink-0"
              >
                {chatLoading ? "Sending…" : "Send"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AiAdvisor;
